
import { Button } from "@/components/ui/button";
import { PhoneCall, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { EnhancedLead } from "@/hooks/use-dashboard-data";
import { LeadStatusSelect } from "@/components/dashboard/leads/LeadStatusSelect";
import { Card } from "@/components/ui/card";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface LeadDetailProps {
  lead?: EnhancedLead;
  isLoading: boolean;
}

export function LeadDetail({ lead, isLoading }: LeadDetailProps) {
  const navigate = useNavigate();
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!lead) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-semibold mb-2">Lead Not Found</h2>
        <Button onClick={() => navigate("/dashboard")}>Return to Dashboard</Button>
      </div>
    );
  }

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      setUpdatingStatus(id);
      const { error } = await supabase
        .from('nursing_home_leads')
        .update({ status: newStatus })
        .eq('id', id);
        
      if (error) throw error;
      
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={() => navigate("/dashboard")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2 p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold mb-2">{lead.nursing_home_name}</h1>
              <p className="text-muted-foreground capitalize">{lead.location}</p>
            </div>
            <LeadStatusSelect
              leadId={lead.id}
              status={lead.status || "interested"}
              updatingStatus={updatingStatus}
              onStatusChange={handleStatusChange}
            />
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="font-medium mb-2">Actions</h3>
              <div className="flex space-x-2">
                <Button variant="outline" asChild>
                  <a href={`/nursing-homes/${lead.location?.toLowerCase()}/${lead.nursing_home_id}`} target="_blank" rel="noopener">
                    View Nursing Home
                  </a>
                </Button>
                
                <Button variant="outline" onClick={() => {
                  toast.info('Contact feature coming soon', { 
                    description: 'This feature will be available in a future update.'
                  });
                }}>
                  <PhoneCall className="h-4 w-4 mr-2" />
                  Contact
                </Button>
              </div>
            </div>

            <div>
              <h3 className="font-medium mb-2">Notes</h3>
              <p className="text-muted-foreground">
                {lead.notes || "No notes added yet."}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-medium mb-4">Details</h3>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm text-muted-foreground">Added On</dt>
              <dd>{format(new Date(lead.created_at), 'MMM d, yyyy')}</dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">Last Updated</dt>
              <dd>{format(new Date(lead.updated_at), 'MMM d, yyyy')}</dd>
            </div>
          </dl>
        </Card>
      </div>
    </div>
  );
}
